import React, { useState } from "react";
import moment from "moment";
import MixpanelService from "../utils/MixpanelService";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { EditOutlined, Cancel, Close } from "@mui/icons-material";
import DeleteOutline from "../Icons/DeleteOutline";
import INTERCOM_ARTICLES from "../../constants/intercomArticles";

const SubmissionCard = ({
  submissionName,
  setSubmissionName,
  submissionPath,
  submitting,
  onSubmit,
  setLeavePageLink,
  claimsDeidentified,
  submissionStats,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftSubmissionName, setDraftSubmissionName] = useState(submissionName);

  const { numFiles, total, selected, submittable, failedToDeIdentify } = submissionStats;
  const hasSelectedRows = selected > 0;

  let filesAttached = "default";
  let rowsSelected = "default";
  let deIdentified = "default";
  let submissionReady = "default";

  if (numFiles > 0) {
    filesAttached = "complete";
  }

  if (filesAttached === "complete" && hasSelectedRows) {
    rowsSelected = "complete";
  } else if (filesAttached === "complete" && !hasSelectedRows) {
    rowsSelected = "error";
  }

  let prevStepsComplete = filesAttached === "complete" && rowsSelected === "complete";

  if (prevStepsComplete && !failedToDeIdentify) {
    deIdentified = "complete";
  } else if (prevStepsComplete && failedToDeIdentify) {
    deIdentified = "error";
  }

  prevStepsComplete =
    filesAttached === "complete" && rowsSelected === "complete" && deIdentified === "complete";

  if (prevStepsComplete && submittable > 0) {
    submissionReady = "complete";
  } else if (prevStepsComplete && submittable === 0) {
    submissionReady = "error";
  }

  const steps = {
    files_attached: {
      status: filesAttached,
      label: "Files Attached",
    },
    rows_selected: {
      status: rowsSelected,
      label: "Rows Selected",
      counts: `${selected}/${total}`,
    },
    claims_deidentified: {
      status: deIdentified,
      label: "Claims De-Identified",
    },
    submissionReady: {
      status: submissionReady,
      label: "Ready for Submission",
      counts: `${submittable}/${selected}`,
    },
  };

  const renderStep = (step) => {
    switch (step.status) {
      case "complete": {
        return (
          <div className="submission-card__body__step submission-card__body__step--done">
            <div className="submission-card__body__step__content">
              <span>{step.label}</span>
              <span className="t--400">{step.counts}</span>
            </div>
          </div>
        );
      }
      case "error": {
        return (
          <div className="submission-card__body__step submission-card__body__step--error">
            <Cancel className="submission-card__body__step__icon" />
            <div className="submission-card__body__step__content">
              <span>{step.label}</span>
              <span className="t--400">{step.counts}</span>
            </div>
          </div>
        );
      }
      default: {
        return (
          <div className="submission-card__body__step submission-card__body__step--default">
            <div className="submission-card__body__step__content">
              <span>{step.label}</span>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <>
      <div className="submission-card">
        <div className="submission-card__header">
          {editing ? (
            <>
              <div className="submission-card__header__name">
                <input
                  type="text"
                  value={draftSubmissionName}
                  onChange={(e) => setDraftSubmissionName(e.target.value)}
                  className="submission-card__header__name__input"
                  autoFocus
                  data-testid="title-field"
                />
              </div>
              <div className="submission-card__header__edit-button">
                <button
                  className="button button--white button--sq-md submission-card__header__button"
                  onClick={() => {
                    setSubmissionName(draftSubmissionName);
                    setEditing(false);
                  }}
                >
                  <SaveOutlinedIcon style={{ fontSize: "16px" }} />
                </button>
                <button
                  className="button button--white button--sq-md submission-card__header__button"
                  onClick={() => setEditing(false)}
                >
                  <Close style={{ fontSize: "16px" }} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="submission-card__header__date">
                <div className="submission-card__header__date__day">{moment().format("DD")}</div>
                <div className="submission-card__header__date__month">{moment().format("MMM")}</div>
              </div>
              <div className="submission-card__header__name">
                <div className="submission-card__header__name__title">Submission Name</div>
                <div className="submission-card__header__name__value">{submissionName}</div>
              </div>
              <div className="submission-card__header__edit-button">
                <button
                  className="button button--white button--sq-md submission-card__header__button"
                  onClick={() => setEditing(true)}
                >
                  <EditOutlined style={{ fontSize: "16px" }} />
                </button>
              </div>
            </>
          )}
        </div>
        <div className="submission-card__body">
          <div className="submission-card__body__steps-container">
            {renderStep(steps.files_attached)}
            <div className="submission-card__body__step__join"></div>
            {renderStep(steps.rows_selected)}
            <div className="submission-card__body__step__join"></div>
            {renderStep(steps.claims_deidentified)}
            <div className="submission-card__body__step__join"></div>
            {renderStep(steps.submissionReady)}
          </div>
        </div>
        <div className="submission-card__footer">
          <div className="submission-card__footer__note">
            Rows with alerts will not be submitted.{" "}
            <a
              href={INTERCOM_ARTICLES.CLAIMS_SUBMISSION_OVERVIEW}
              target="_blank"
              className="t--anchor t--blue"
              onClick={() => {
                MixpanelService.track("esp_submissions_learn_more");
              }}
            >
              Learn more
            </a>
          </div>
          <div className="submission-card__footer__actions">
            <button
              className="button button--white button--sq-lg"
              style={{ padding: "10px" }}
              onClick={(e) => {
                e.preventDefault();
                setLeavePageLink(submissionPath);
              }}
            >
              <DeleteOutline />
            </button>
            <button
              className="button button--lg button--fw button--blue"
              id={steps.submissionReady.status === "complete" ? "saveAndSubmit" : null}
              disabled={steps.submissionReady.status != "complete"}
              onClick={onSubmit}
            >
              {submitting ? <div className="loading-indicator"></div> : "Save and Submit"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubmissionCard;
